/**
 * 单选组：要么选中一个 checkbox，要么手动输入"其他"文本，二者互斥，只能有一个值
 */
export interface RadioWithText {
  /** 选中的 checkbox tag，未选为 null */
  selected: string | null
  /** "其他"输入框的文本 */
  otherText: string
}

/**
 * 纯 checkbox 单选组：只能选中一个
*/
export type RadioOnly = string | null

export interface FaceTemplate {
  // ── 一、输入行 ──
  siteName: string
  mileage: string
  reportCode: string
  kwkd: number
  kwgd: number
  kwmj: number
  kwff: string
  other: string

  // ── 二、选择与输入（单选 checkbox + 其他文本） ──
  zzmzt: RadioWithText   // 掌子面状态
  mkwzt: RadioWithText   // 毛开挖面状态
  ytqd: RadioWithText    // 岩石强度
  fhcd: RadioWithText    // 风化程度
  yszt: RadioWithText    // 涌水状态
  yrfycd: RadioWithText  // 岩溶发育程度

  // ── 二、纯 checkbox 单选 ──
  lxkd: RadioOnly        // 裂隙宽度
  lxzt: RadioOnly        // 裂隙状态

  // ── 三、下拉框 ──
  designLevel: string
  constructLevel: string

  // ── 四、图像 ──
  sumiao: string
  facePicture: string

  // ── 五、富文本 ──
  dzms: string
  risk: string

  // ── 六、表格 ──
  datetime: string
}

// ── 里程 ──

export const MILEAGE_REGEX = /^[A-Za-z]+\d+\+\d{1,3}(\.\d{1,3})?$/

// export function mileageToNumber(mileage: string): number | null {
//   const match = mileage.match(/^([A-Za-z]+)(\d+)\+(\d+(?:\.\d+)?)$/)
//   if (!match) return null
//   const km = parseInt(match[2], 10)
//   const m = parseFloat(match[3])
//   return km * 1000 + m
// }

export function numberToMileage(num: number, prefix: string = 'DK'): string {
  const km = Math.floor(num / 1000)
  const remainder = num % 1000
  if (Number.isInteger(remainder)) {
    return `${prefix}${km}+${String(remainder).padStart(3, '0')}`
  }
  const intPart = Math.floor(remainder)
  const decPart = remainder - intPart
  const intStr = String(intPart).padStart(3, '0')
  const decStr = decPart
    .toFixed(3)
    .substring(2)
    .replace(/0+$/, '')
  return `${prefix}${km}+${intStr}.${decStr}`
}

export function isValidMileage(value: string): boolean {
  return MILEAGE_REGEX.test(value)
}

export function isValidNumber(value: string): boolean {
  if (value.trim() === '') return false
  const num = Number(value)
  return !isNaN(num) && isFinite(num)
}

// ── 罗马数字级别 ──

export const ROMAN_LEVELS = ['Ⅰ', 'Ⅱ', 'Ⅲ', 'Ⅳ', 'Ⅴ', 'Ⅵ']

export function isValidRomanLevel(value: string): boolean {
  return ROMAN_LEVELS.includes(value)
}

// ── checkbox 选项 tag 常量 ──

export const ZZMZT_OPTIONS = ['zzmzt_wd', 'zzmzt_zmdk', 'zzmzt_zmjc', 'zzmzt_zzbnzw'] as const
export const MKWZT_OPTIONS = ['mkwzt_zw', 'mkwzt_scdk', 'mkwzt_jszh', 'mkwzt_cqzh'] as const
export const YTQD_OPTIONS = ['ytqd_3060', 'ytqd_1530', 'ytqd_0515', 'ytqd_lt5'] as const
export const FHCD_OPTIONS = ['fhcd_wfh', 'fhcd_rfh', 'fhcd_qfh', 'fhcd_qqfh'] as const
export const LXKD_OPTIONS = ['lxkd_gt5', 'lxkd_3to5', 'lxkd_1to3', 'lxkd_lt1', 'lxkd_bycq'] as const
export const LXZT_OPTIONS = ['lxzt_fy', 'lxzt_wzjb', 'lxzt_qzcc', 'lxzt_wtc', 'lxzt_bycq'] as const
export const YSZT_OPTIONS = ['yszt_gz', 'yszt_sr', 'yszt_oyss', 'yszt_jcss'] as const
export const YRFYCD_OPTIONS = ['yrfycd_wei', 'yrfycd_ruo', 'yrfycd_zd', 'yrfycd_ql'] as const

export const ZZMZT_TEXT_TAG = 'zzmzt_qt'
export const MKWZT_TEXT_TAG = 'mkwzt_qt'
export const YTQD_TEXT_TAG = 'ytqd_qt'
export const FHCD_TEXT_TAG = 'fhcd_qt'
export const YSZT_TEXT_TAG = 'yszt_qt'
export const YRFYCD_TEXT_TAG = 'yrfycd_qt'

// ── 选项中文标签映射（用于验证消息显示） ──

export const OPTION_LABELS: Record<string, string> = {
  // 掌子面状态
  zzmzt_wd: '稳定', zzmzt_zmdk: '正面掉块', zzmzt_zmjc: '正面挤出', zzmzt_zzbnzw: '正面不稳定',
  // 毛开挖面状态
  mkwzt_zw: '自稳', mkwzt_scdk: '松弛掉块', mkwzt_jszh: '自稳困难需及时支护', mkwzt_cqzh: '要超前支护',
  // 岩石强度
  ytqd_3060: '30<Rc<60', ytqd_1530: '15<Rc<30', ytqd_0515: '5<Rc<15', ytqd_lt5: 'Rc<5',
  // 风化程度
  fhcd_wfh: '微风化', fhcd_rfh: '弱风化', fhcd_qfh: '强风化', fhcd_qqfh: '全风化',
  // 裂隙宽度
  lxkd_gt5: '>5', lxkd_3to5: '3~5', lxkd_1to3: '1~3', lxkd_lt1: '<1', lxkd_bycq: '不易测取',
  // 裂隙状态
  lxzt_fy: '发育', lxzt_wzjb: '微张密闭', lxzt_qzcc: '平直粗糙', lxzt_wtc: '无充填', lxzt_bycq: '不易测取',
  // 涌水状态
  yszt_gz: '干燥', yszt_sr: '湿润', yszt_oyss: '偶有渗水', yszt_jcss: '经常渗水',
  // 岩溶发育程度
  yrfycd_wei: '微', yrfycd_ruo: '弱', yrfycd_zd: '中等', yrfycd_ql: '强烈',
}

export const GROUP_LABELS: Record<string, string> = {
  zzmzt: '掌子面状态',
  mkwzt: '毛开挖面状态',
  ytqd: '岩石强度',
  fhcd: '风化程度',
  lxkd: '裂隙宽度',
  lxzt: '裂隙状态',
  yszt: '涌水状态',
  yrfycd: '岩溶发育程度',
}

// ── 空模板 ──

export function createEmptyFaceTemplate(): FaceTemplate {
  return {
    siteName: '',
    mileage: '',
    reportCode: '',
    kwkd: 0,
    kwgd: 0,
    kwmj: 0,
    kwff: '',
    other: '',

    zzmzt: { selected: null, otherText: '' },
    mkwzt: { selected: null, otherText: '' },
    ytqd: { selected: null, otherText: '' },
    fhcd: { selected: null, otherText: '' },
    yszt: { selected: null, otherText: '' },
    yrfycd: { selected: null, otherText: '' },
    lxkd: null,
    lxzt: null,

    designLevel: '',
    constructLevel: '',

    sumiao: '',
    facePicture: '',

    dzms: '',
    risk: '',

    datetime: '',
  }
}

// ── 字段填写检查 ──

export type FaceTemplateFieldKey = keyof FaceTemplate

export interface FieldCheckResult {
  key: FaceTemplateFieldKey
  label: string
  filled: boolean
  required: boolean
}

const RADIO_WITH_TEXT_KEYS: FaceTemplateFieldKey[] = ['zzmzt', 'mkwzt', 'ytqd', 'fhcd', 'yszt', 'yrfycd']
const RADIO_ONLY_KEYS: FaceTemplateFieldKey[] = ['lxkd', 'lxzt']

function isRadioWithTextFilled(field: RadioWithText): boolean {
  return field.selected !== null || field.otherText.trim() !== ''
}

export const FACE_TEMPLATE_FIELD_META: {
  key: FaceTemplateFieldKey
  label: string
  required: boolean
}[] = [
    { key: 'siteName', label: '工点名称', required: true },
    { key: 'mileage', label: '掌子面里程', required: true },
    { key: 'reportCode', label: '预报报告编号', required: true },
    { key: 'kwkd', label: '开挖宽度', required: true },
    { key: 'kwgd', label: '开挖高度', required: true },
    { key: 'kwmj', label: '开挖面积', required: true },
    { key: 'kwff', label: '开挖方式', required: true },
    { key: 'other', label: '其他', required: false },

    { key: 'zzmzt', label: '掌子面状态', required: true },
    { key: 'mkwzt', label: '毛开挖面状态', required: true },
    { key: 'ytqd', label: '岩石强度', required: true },
    { key: 'fhcd', label: '风化程度', required: true },
    { key: 'lxkd', label: '裂隙宽度', required: true },
    { key: 'lxzt', label: '裂隙状态', required: true },
    { key: 'yszt', label: '涌水状态', required: true },
    { key: 'yrfycd', label: '岩溶发育程度', required: true },

    { key: 'designLevel', label: '围岩设计级别', required: true },
    { key: 'constructLevel', label: '施工围岩级别', required: true },

    { key: 'sumiao', label: '素描图', required: true },
    { key: 'facePicture', label: '掌子面照片', required: true },

    { key: 'dzms', label: '地质素描', required: true },
    { key: 'risk', label: '风险提示', required: true },

    { key: 'datetime', label: '日期', required: true },
  ]

/**
 * 检查每个字段是否已填写（用于计算完成度）
 */
export function validateFaceTemplate(template: FaceTemplate): FieldCheckResult[] {
  return FACE_TEMPLATE_FIELD_META.map(({ key, label, required }) => {
    let filled = false
    const value = template[key]

    if (RADIO_WITH_TEXT_KEYS.includes(key)) {
      filled = isRadioWithTextFilled(value as RadioWithText)
    } else if (RADIO_ONLY_KEYS.includes(key)) {
      filled = (value as RadioOnly) !== null
    } else if (key === 'mileage') {
      filled = isValidMileage(value as string)
    } else if (key === 'kwkd' || key === 'kwgd' || key === 'kwmj') {
      filled = typeof value === 'number' && isFinite(value) && value > 0
    } else if (key === 'designLevel' || key === 'constructLevel') {
      filled = isValidRomanLevel(value as string)
    } else {
      filled = (value as string).trim() !== ''
    }

    return { key, label, filled, required }
  })
}

// ── 提交验证 ──

export interface ValidationError {
  key: FaceTemplateFieldKey
  label: string
  message: string
}

/**
 * 提交前的业务规则验证：
 * - 必填字段不能为空
 * - 单选组 checkbox+文本 只能选一个 checkbox 或填一个文本，不能同时有值
 * - 数字字段必须是合法数字
 * - 里程格式必须正确
 */
export function validateForSubmit(template: FaceTemplate): ValidationError[] {
  const errors: ValidationError[] = []

  // 必填检查
  const fieldResults = validateFaceTemplate(template)
  for (const f of fieldResults) {
    if (f.required && !f.filled) {
      errors.push({ key: f.key, label: f.label, message: `${f.label}未填写` })
    }
  }

  // 单选互斥检查：checkbox + 文本不能同时有值
  for (const key of RADIO_WITH_TEXT_KEYS) {
    const field = template[key] as RadioWithText
    if (field.selected !== null && field.otherText.trim() !== '') {
      const groupLabel = GROUP_LABELS[key] || key
      errors.push({
        key,
        label: groupLabel,
        message: `${groupLabel}：已勾选"${OPTION_LABELS[field.selected] || field.selected}"，又填写了其他文本"${field.otherText}"，只能二选一`,
      })
    }
  }

  // 数字格式检查
  for (const key of ['kwkd', 'kwgd', 'kwmj'] as FaceTemplateFieldKey[]) {
    const v = template[key] as number
    if ((v === 0 || v > 0) && !isFinite(v)) {
      const meta = FACE_TEMPLATE_FIELD_META.find((m) => m.key === key)
      errors.push({ key, label: meta?.label || key, message: `${meta?.label || key}必须是有效数字` })
    }
  }

  // 里程格式检查
  if (template.mileage.trim() !== '' && !isValidMileage(template.mileage)) {
    errors.push({ key: 'mileage', label: '掌子面里程', message: '里程格式不正确，示例：DK1+100 或 DK0+002.3' })
  }

  return errors
}
